import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy } from 'firebase/firestore';
import { toast } from 'react-toastify';
import Dashboard from './Dashboard';

const CastManager = () => {
  const [movies, setMovies] = useState([]);
  const [selectedMovie, setSelectedMovie] = useState('');
  const [castMembers, setCastMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCast, setEditingCast] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedMovieForAdd, setSelectedMovieForAdd] = useState('');

  // Multiple cast members form
  const [castForms, setCastForms] = useState([{
    id: Date.now(),
    role: 'Hero',
    customRole: '',
    name: '',
    bio: '',
    link: '',
    priority: 1,
    image: null,
    imageUrl: ''
  }]);

  useEffect(() => {
    fetchMovies();
  }, []);

  useEffect(() => {
    if (selectedMovie) {
      fetchCastMembers();
    }
  }, [selectedMovie]);

  const fetchMovies = async () => {
    try {
      const moviesQuery = query(collection(db, 'movies'), orderBy('title', 'asc'));
      const snapshot = await getDocs(moviesQuery);
      const moviesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMovies(moviesList);
    } catch (error) {
      console.error('Error fetching movies:', error);
      toast.error('Failed to load movies');
    } finally {
      setLoading(false);
    }
  };

  const fetchCastMembers = async () => {
    try {
      const castQuery = query(
        collection(db, 'movieCast'),
        where('movieId', '==', selectedMovie),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(castQuery);
      const castList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCastMembers(castList);
    } catch (error) {
      console.error('Error fetching cast:', error);
      toast.error('Failed to load cast members');
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('project', 'mms');
    formData.append('type', 'cast');

    try {
      const response = await fetch('https://imageapi.magicmoonstudios.com/upload.php', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedMovieForAdd) {
      toast.error('Please select a movie');
      return;
    }

    // Validate all forms
    for (let i = 0; i < castForms.length; i++) {
      const form = castForms[i];
      if (!form.name.trim()) {
        toast.error(`Please enter name for cast member ${i + 1}`);
        return;
      }
      if (form.role === 'Other' && !form.customRole.trim()) {
        toast.error(`Please enter custom role for cast member ${i + 1}`);
        return;
      }
      if (!form.image && !form.imageUrl) {
        toast.error(`Please select image for cast member ${i + 1}`);
        return;
      }
    }

    setUploading(true);

    try {
      // Process all cast members
      for (const form of castForms) {
        let imageUrl = form.imageUrl;

        // Upload new image if selected
        if (form.image) {
          imageUrl = await uploadImage(form.image);
        }

        const castData = {
          movieId: selectedMovieForAdd,
          movieTitle: movies.find(m => m.id === selectedMovieForAdd)?.title || '',
          role: form.role === 'Other' ? form.customRole : form.role,
          name: form.name,
          bio: form.bio,
          link: form.link,
          priority: form.priority,
          imageUrl: imageUrl,
          createdAt: new Date(),
        };

        await addDoc(collection(db, 'movieCast'), castData);
      }

      toast.success(`${castForms.length} cast member(s) added successfully!`);

      // Reset forms
      setCastForms([{
        id: Date.now(),
        role: 'Hero',
        customRole: '',
        name: '',
        bio: '',
        link: '',
        priority: 1,
        image: null,
        imageUrl: ''
      }]);
      setShowAddModal(false);
      setSelectedMovieForAdd('');
      fetchCastMembers();
    } catch (error) {
      console.error('Error saving cast members:', error);
      toast.error('Failed to save cast members');
    } finally {
      setUploading(false);
    }
  };

  const addMoreCastForm = () => {
    setCastForms([...castForms, {
      id: Date.now(),
      role: 'Hero',
      customRole: '',
      name: '',
      bio: '',
      link: '',
      priority: castForms.length + 1,
      image: null,
      imageUrl: ''
    }]);
  };

  const removeCastForm = (id) => {
    if (castForms.length === 1) {
      toast.error('At least one cast member form is required');
      return;
    }
    setCastForms(castForms.filter(form => form.id !== id));
  };

  const updateCastForm = (id, field, value) => {
    setCastForms(castForms.map(form => 
      form.id === id ? { ...form, [field]: value } : form
    ));
  };

  const handleEdit = (cast) => {
    setEditingCast(cast);
    setSelectedMovie(cast.movieId);
    setSelectedMovieForAdd(cast.movieId);
    setCastForms([{
      id: Date.now(),
      role: ['Hero', 'Heroine', 'Director', 'Producer', 'Camera Men'].includes(cast.role) ? cast.role : 'Other',
      customRole: ['Hero', 'Heroine', 'Director', 'Producer', 'Camera Men'].includes(cast.role) ? '' : cast.role,
      name: cast.name,
      bio: cast.bio || '',
      link: cast.link || '',
      priority: cast.priority || 1,
      image: null,
      imageUrl: cast.imageUrl
    }]);
    setShowAddModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!editingCast) return;

    const form = castForms[0];
    if (!form.name.trim()) {
      toast.error('Please enter cast member name');
      return;
    }

    setUploading(true);

    try {
      let imageUrl = form.imageUrl;

      if (form.image) {
        imageUrl = await uploadImage(form.image);
      }

      const castData = {
        movieId: selectedMovieForAdd || selectedMovie,
        movieTitle: movies.find(m => m.id === (selectedMovieForAdd || selectedMovie))?.title || '',
        role: form.role === 'Other' ? form.customRole : form.role,
        name: form.name,
        bio: form.bio,
        link: form.link,
        priority: form.priority,
        imageUrl: imageUrl,
        createdAt: new Date(),
      };

      await updateDoc(doc(db, 'movieCast', editingCast.id), castData);
      toast.success('Cast member updated successfully!');

      setCastForms([{
        id: Date.now(),
        role: 'Hero',
        customRole: '',
        name: '',
        bio: '',
        link: '',
        priority: 1,
        image: null,
        imageUrl: ''
      }]);
      setShowAddModal(false);
      setEditingCast(null);
      setSelectedMovieForAdd('');
      fetchCastMembers();
    } catch (error) {
      console.error('Error updating cast member:', error);
      toast.error('Failed to update cast member');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (castId, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this cast member?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'movieCast', castId));
      toast.success('Cast member deleted successfully!');
      fetchCastMembers();
    } catch (error) {
      console.error('Error deleting cast member:', error);
      toast.error('Failed to delete cast member');
    }
  };

  return (
    <Dashboard>
      <div className="admin-content">
        <div className="content-header">
          <h1>Movie Cast Manager</h1>
          <button className="add-btn" onClick={() => setShowAddModal(true)}>
            + Add Cast Member
          </button>
        </div>

        <div className="info-box" style={{ marginBottom: '24px' }}>
          <p>Manage cast and crew members for your movies. Click "Add Cast Member" to start adding new members, or select a movie below to view existing cast.</p>
        </div>

        {/* Movie Selection for Viewing */}
        <div className="form-group" style={{ maxWidth: '500px', marginBottom: '30px' }}>
          <label>Select Movie to View Cast</label>
          <select
            value={selectedMovie}
            onChange={(e) => setSelectedMovie(e.target.value)}
            style={{
              padding: '12px',
              fontSize: '16px',
              borderRadius: '8px',
              border: '2px solid #ddd',
              width: '100%'
            }}
          >
            <option value="">-- Choose a Movie --</option>
            {movies.map(movie => (
              <option key={movie.id} value={movie.id}>
                {movie.title} ({movie.year || movie.releaseDate})
              </option>
            ))}
          </select>
        </div>

        {/* Cast Members List */}
        {loading ? (
          <div className="loading-message">Loading...</div>
        ) : !selectedMovie ? (
          <div className="empty-state">
            <p style={{ fontSize: '18px', color: '#666' }}>👆 Select a movie to view and manage its cast</p>
          </div>
        ) : castMembers.length === 0 ? (
          <div className="empty-state">
            <p style={{ fontSize: '18px', color: '#666' }}>No cast members added yet. Click "Add Cast Member" to get started!</p>
          </div>
        ) : (
          <div className="banners-grid">
            {castMembers.map((cast) => (
              <div key={cast.id} className="banner-item">
                <div className="banner-preview" style={{ height: 'auto', minHeight: '300px' }}>
                  <img src={cast.imageUrl} alt={cast.name} style={{ width: '100%', height: 'auto', objectFit: 'contain' }} />
                  <div className="banner-overlay">
                    <span className="media-type-badge">{cast.role}</span>
                  </div>
                </div>
                <div className="banner-details">
                  <h3>{cast.name}</h3>
                  <p className="subtitle" style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>Priority: {cast.priority || 'N/A'}</p>
                  {cast.bio && <p className="subtitle" style={{ fontSize: '13px', marginTop: '8px' }}>{cast.bio.substring(0, 100)}{cast.bio.length > 100 ? '...' : ''}</p>}
                  {cast.link && (
                    <p className="button-info">
                      🔗 <a href={cast.link} target="_blank" rel="noopener noreferrer" style={{ color: '#4A90E2' }}>{cast.link}</a>
                    </p>
                  )}
                  <div className="banner-actions" style={{ marginTop: '15px' }}>
                    <button className="edit-btn" onClick={() => handleEdit(cast)}>
                      Edit
                    </button>
                    <button className="delete-btn" onClick={() => handleDelete(cast.id, cast.imageUrl)}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="modal-overlay" onClick={() => { setShowAddModal(false); setEditingCast(null); }}>
            <div className="modal-content banner-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div className="modal-header">
                <h2>{editingCast ? 'Edit Cast Member' : 'Add Cast Members'}</h2>
                <button className="close-btn" onClick={() => { setShowAddModal(false); setEditingCast(null); setSelectedMovieForAdd(''); }}>✕</button>
              </div>

              <form onSubmit={editingCast ? handleUpdate : handleSubmit} className="banner-form">
                {/* Movie Selection in Modal */}
                <div className="form-group" style={{ background: 'linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%)', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
                  <label style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Select Movie *</label>
                  <select
                    value={editingCast ? selectedMovie : selectedMovieForAdd}
                    onChange={(e) => editingCast ? setSelectedMovie(e.target.value) : setSelectedMovieForAdd(e.target.value)}
                    style={{
                      padding: '12px',
                      fontSize: '16px',
                      borderRadius: '8px',
                      border: '2px solid #3b82f6',
                      width: '100%',
                      background: 'white'
                    }}
                    required
                  >
                    <option value="">-- Choose a Movie --</option>
                    {movies.map(movie => (
                      <option key={movie.id} value={movie.id}>
                        {movie.title} ({movie.year || movie.releaseDate})
                      </option>
                    ))}
                  </select>
                </div>

                {castForms.map((form, index) => (
                  <div key={form.id} style={{ border: '2px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginBottom: '20px', background: '#f8fafc' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h3 style={{ margin: 0, color: '#1e293b', fontSize: '18px' }}>Cast Member {index + 1}</h3>
                      {!editingCast && castForms.length > 1 && (
                        <button type="button" onClick={() => removeCastForm(form.id)} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Priority Number *</label>
                      <input
                        type="number"
                        min="1"
                        placeholder="Display order (1, 2, 3...)"
                        value={form.priority}
                        onChange={(e) => updateCastForm(form.id, 'priority', parseInt(e.target.value) || 1)}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Role *</label>
                      <select
                        value={form.role}
                        onChange={(e) => updateCastForm(form.id, 'role', e.target.value)}
                        required
                      >
                        <option value="Hero">Hero</option>
                        <option value="Heroine">Heroine</option>
                        <option value="Director">Director</option>
                        <option value="Producer">Producer</option>
                        <option value="Camera Men">Camera Men</option>
                        <option value="Other">Other (Custom)</option>
                      </select>
                    </div>

                    {form.role === 'Other' && (
                      <div className="form-group">
                        <label>Custom Role *</label>
                        <input
                          type="text"
                          placeholder="e.g., Music Director, Editor, etc."
                          value={form.customRole}
                          onChange={(e) => updateCastForm(form.id, 'customRole', e.target.value)}
                          required={form.role === 'Other'}
                        />
                      </div>
                    )}

                    <div className="form-group">
                      <label>Name *</label>
                      <input
                        type="text"
                        placeholder="e.g., John Doe"
                        value={form.name}
                        onChange={(e) => updateCastForm(form.id, 'name', e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Bio (Optional)</label>
                      <textarea
                        rows="3"
                        placeholder="Brief description about the person..."
                        value={form.bio}
                        onChange={(e) => updateCastForm(form.id, 'bio', e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label>Link (Optional)</label>
                      <input
                        type="url"
                        placeholder="https://example.com or social media link"
                        value={form.link}
                        onChange={(e) => updateCastForm(form.id, 'link', e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label>Profile Image *</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => updateCastForm(form.id, 'image', e.target.files[0])}
                        required={!form.imageUrl}
                      />
                      {form.imageUrl && (
                        <div style={{ marginTop: '10px' }}>
                          <img src={form.imageUrl} alt="Current" style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {!editingCast && (
                  <button 
                    type="button" 
                    onClick={addMoreCastForm}
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '8px', 
                      fontSize: '15px', 
                      fontWeight: '600', 
                      cursor: 'pointer', 
                      marginBottom: '20px',
                      transition: 'all 0.3s'
                    }}
                    onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                    onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                  >
                    + Add 1 More Cast Member
                  </button>
                )}

                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={() => { setShowAddModal(false); setEditingCast(null); }}>
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn" disabled={uploading}>
                    {uploading ? 'Saving...' : (editingCast ? 'Update Cast Member' : `Save ${castForms.length} Cast Member(s)`)}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Dashboard>
  );
};

export default CastManager;
